import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  PoLifecycleColumns,
  PoLifecycleItemLevelAuditColumns,
} from "components/DataGrid/columnDefinitions";
import { DataGrid } from "components/DataGrid/DataGrid";
import { PAGE, PO_REASON } from "../common/CONSTANTS";
import { useLocation, useNavigate } from "react-router-dom";
import { LabelWithControl } from "components/common/LabelControl";
import { useDispatch, useSelector } from "react-redux";
import { encryptString } from "components/common/EncryptDecrypt";
import { searchPO, searchPOReset } from "redux/actions/searchPOAction";
import {
  convertToCamelCase,
  getDestinationName,
  getFormattedDate,
} from "components/common/Utility";

export const ManualPurchaseOrderLifecycle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { orderId } = location.state;
  const userType = useSelector((state) => state.user);
  const loginResponse = userType.loginResponse;
  const { locationResponse } = useSelector((state) => state.locations);
  const { searchPOResponse, isSearchLoading, isSearchSuccess } = useSelector(
    (state) => state.searchPO
  );
  const { reasonCodesResponse } = useSelector((state) => state.reasonCodes);
  const [poLifecycle, setPOLifecycle] = useState();
  const [userActionHistory, setUserActionHistory] = useState([]);
  const [itemHistory, setItemHistory] = useState([]);

  useEffect(() => {
    const searchCriteria = {
      extOrderId: orderId,
    };
    callFetchOrders(searchCriteria);
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSearchSuccess) {
      const data = { ...searchPOResponse.data };
      if (data.shipToLocationName) {
        setPOLifecycle(searchPOResponse.data);
      } else {
        setPOLifecycle({
          ...data,
          shipToLocationName: getDestinationName(
            locationResponse,
            data.shipToLocationId
          ),
        });
      }
    }
  }, [searchPOResponse, isSearchSuccess, locationResponse]);

  useEffect(() => {
    if (poLifecycle) {
      const userHistory = [];
      poLifecycle.userActionsList?.forEach((element) => {
        const reasonCodes = reasonCodesResponse.reasonCodes.filter(
          (item) =>
            item.orderType === PO_REASON.ORDER_TYPE &&
            item.reasonCode === element.reasonCode
        );
        userHistory.push({
          ...element,
          reasonCodeName:
            reasonCodes.length > 0 ? reasonCodes[0].description : "",
        });
      });
      setUserActionHistory(userHistory);
      setItemHistory(poLifecycle.itemsByExtOrderIds);
      dispatch(searchPOReset());
    }
  }, [poLifecycle, dispatch, reasonCodesResponse.reasonCodes]);

  const callFetchOrders = async (searchCriteria) => {
    const encryptedEmailId = await encryptString(loginResponse?.email);
    const payload = {
      params: {
        purchaseOrder: searchCriteria,
        userId: encryptedEmailId,
      },
      requestType: "lifecycle",
      limit: 10,
      offset: 0,
    };
    dispatch(searchPO(payload));
  };

  const handleBackPress = () => {
    navigate(PAGE.PO_SUMMARY.url, {
      state: {
        retainSearchCriteria: true,
      },
    });
  };

  return (
    <div className="mainPageStyling">
      {isSearchLoading && (
        <Backdrop
          sx={{
            color: "#fff",
            flexDirection: "column",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={true}
        >
          <CircularProgress color="inherit" />

          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Loading...
          </Typography>
        </Backdrop>
      )}
      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
          {PAGE.PO_LIFECYCLE.label}
        </Typography>
      </div>

      <div className="poHeaderStyling">
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Grid container spacing={2} sx={{ "& > *": { flex: 1 } }}>
            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="PO Identifier" labelId="poIdentifier">
                <Typography id="poIdentifier">
                  {poLifecycle?.orderId ? poLifecycle?.orderId : poLifecycle?.extOrderId}
                </Typography>
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="Supplier" labelId="supplier">
                <Typography id="supplier">
                  {poLifecycle?.supplierId}{poLifecycle?.supplierName ? ` ${poLifecycle.supplierName}` : ""}
                </Typography>
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="Destination" labelId="destination">
                <Typography id="destination">
                  {`${poLifecycle?.shipToLocationId} ${poLifecycle?.shipToLocationName}`}
                </Typography>
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="Order Date" labelId="orderDate">
                <Typography id="orderDate">
                  {getFormattedDate(poLifecycle?.orderDate)}
                </Typography>
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="Delivery Date" labelId="deliveryDate">
                <Typography id="deliveryDate">
                  {getFormattedDate(poLifecycle?.deliveryDate)}
                </Typography>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <LabelWithControl label="Current Status" labelId="currentStatus">
                <Typography id="currentStatus">
                  {convertToCamelCase(poLifecycle?.status)}
                </Typography>
              </LabelWithControl>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <div style={{ width: "100%", height: "125px" }}>
        <DataGrid
          columnDefs={PoLifecycleColumns}
          rowData={userActionHistory}
          defaultColDef={{
            flex: 1,
            sortable: true,
            filter: false,
            resizable: false,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            headerClass: "header-wrap",
            autoSizeStrategy: { type: "fitCellContents" },
          }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
        <Typography component="h1" variant="h6" sx={{ mb: 2, mr: 2 }}>
          Item Level Audit
        </Typography>
      </div>
      <div style={{ width: "100%", height: "calc( 100vh - 460px)" }}>
        <DataGrid
          columnDefs={PoLifecycleItemLevelAuditColumns}
          rowData={itemHistory}
          defaultColDef={{
            flex: 1,
            sortable: false,
            filter: false,
            resizable: false,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            headerClass: "header-wrap",
            autoSizeStrategy: { type: "fitCellContents" },
          }}
        />
      </div>
      <div className="parentPoButtons">
        <Button
          className="commonPOButtons"
          variant="contained"
          color="secondary"
          onClick={handleBackPress}
        >
          Back
        </Button>
      </div>
    </div>
  );
};
